import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import "../styles/AdminTestimonials.css";

type NewsStatus = "active" | "inactive";
type ModalMode = "add" | "edit" | "view";

interface LatestNewsRow {
  id: number;
  title: string;
  short_description: string;
  full_description: string;
  image: any;
  link: string;
  publish_date: string;
  sort_order: number;
  status: NewsStatus;
  createdAt: string;
  updatedAt: string;
}

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

export default function AdminLatestNews() {
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [data, setData] = useState<LatestNewsRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<LatestNewsRow | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const newsFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      { key: "title", label: "Title", type: "text", colSpan: 2 },
      {
        key: "short_description",
        label: "Short Description",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "full_description",
        label: "Full Description",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "image",
        label: "Image",
        type: "image",
        imageType: "single",
        imageFolder: "latestnews",
        uploadField: "latestNewsImage",
        colSpan: 1,
      },
      { key: "link", label: "Link", type: "text", colSpan: 2 },
      {
        key: "publish_date",
        label: "Publish Date",
        type: "date",
        colSpan: 1,
      },
      {
        key: "sort_order",
        label: "Sort Order",
        type: "number",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "title", label: "Title" },
    {
      key: "image",
      label: "Image",
      imageFolder: "latestnews",
    },
    {
      key: "short_description",
      label: "Short Description",
      render: (row) => (
        <span className="admin-testimonial-text-clamp">
          {row.short_description}
        </span>
      ),
    },
    {
      key: "publish_date",
      label: "Publish Date",
      render: (row) =>
        row.publish_date ? formatDateToDMY(row.publish_date) : "-",
    },
    { key: "sort_order", label: "Sort Order" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
    },
  ];

  const fetchLatestNews = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/latest-news`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Latest News Error:", error);
    }
  };

  useEffect(() => {
    fetchLatestNews();
  }, []);

  const buildFormData = (formData: any, fields: FormField[]) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      if (field.type === "image") {
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value?.removed || value === "") {
          form.append(field.key, "REMOVE");
        } else if (field.key !== "image") {
          form.append(field.key, value ?? "");
        }
      } else {
        form.append(field.key, value ?? "");
      }
    });

    return form;
  };

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, newsFields);

      const method = modalMode === "add" ? "POST" : "PUT";
      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/latest-news`
          : `${BASE_URL}/admin/latest-news/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        body: form,
      });

      const result = await res.json();

      if (result.success) {
        fetchLatestNews();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Latest news added successfully."
              : "Latest news updated successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to save latest news.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Submit Error:", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving latest news.",
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete News",
      message: `Are you sure you want to delete "${row.title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_URL}/admin/latest-news/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchLatestNews();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Latest news deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Error:", error);
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Latest News"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="latest-news"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Latest News"
        fields={newsFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
